/**
 * Pull a human-readable message out of whatever shape `useApi*` threw.
 *
 * The API hooks throw an `ApiRequestError` (a real `Error` subclass) whose
 * `message` is already the precedence-resolved reason, so the `instanceof
 * Error` branch below is the fast path. The object branches still matter: a
 * raw ProblemDetails body, a hand-rolled `{ error: "…" }` envelope, or a test
 * fixture can reach a call site without passing through the hook, and those
 * are plain objects that `String(error)` would collapse to
 * `"[object Object]"`. Precedence matches the gateway's
 * surfaces: ASP.NET Core ProblemDetails (`detail` → `title`), then `ApiError`
 * (`message`), then the legacy module envelope (`error`), then `statusText`.
 *
 * Kept hook-free in its own module so it is unit-testable under Node's
 * `--experimental-strip-types` runner; `useApi.ts` re-exports it.
 */
export function extractErrorMessage(error: unknown): string {
  if (!error) return 'Unknown error';
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;
  if (typeof error === 'object') {
    const e = error as Record<string, unknown>;
    // ASP.NET Core ProblemDetails surface
    if (typeof e.detail === 'string' && e.detail.length > 0) return e.detail as string;
    if (typeof e.title === 'string' && e.title.length > 0) return e.title as string;
    if (typeof e.message === 'string' && e.message.length > 0) return e.message as string;
    // Legacy hand-rolled module envelope: `new { error = "..." }` (H-1).
    if (typeof e.error === 'string' && e.error.length > 0) return e.error as string;
    if (typeof e.statusText === 'string' && e.statusText.length > 0) return e.statusText as string;
    try { return JSON.stringify(error).slice(0, 300); } catch { /* fall through */ }
  }
  return String(error);
}
